/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect } from "react";
import ThemedToggle from "../common/ThemedToggle";
import ConstraintCard from "./ConstraintCard";
import FieldPillPicker from "./FieldPillPicker";

/**
 * Props:
 *   value            — array of { name, fields: [], unique: bool }
 *   onChange         — (newArray) => void
 *   availableColumns — array of { name, data_type }
 *   onEditingChange  — (bool) => void
 * Note: composite indexes are NOT gated on populated tables.
 */
export default function CompositeIndexBuilder({ value = [], onChange, availableColumns = [], onEditingChange }) {
  // editing = { index, data: { name, fields, unique } } | null
  const [editing, setEditing] = useState(null);

  useEffect(() => { onEditingChange && onEditingChange(editing !== null); }, [editing, onEditingChange]);

  const valid = editing && (editing.data.fields || []).length >= 1;
  const saveHint = "Select at least one field.";

  function startNew() { setEditing({ index: "new", data: { name: "", fields: [], unique: false } }); }
  function startEdit(idx) {
    const r = value[idx];
    setEditing({ index: idx, data: { name: r.name || "", fields: [...(r.fields || [])], unique: !!r.unique } });
  }
  function cancel() { setEditing(null); }
  function remove(idx) { onChange(value.filter((_, i) => i !== idx)); }
  function patch(p) { setEditing(e => ({ ...e, data: { ...e.data, ...p } })); }
  function toggle(col) {
    const cur = editing.data.fields || [];
    patch({ fields: cur.includes(col) ? cur.filter(f => f !== col) : [...cur, col] });
  }
  function save() {
    if (!valid) return;
    const row = { name: (editing.data.name || "").trim(), fields: editing.data.fields, unique: editing.data.unique };
    if (editing.index === "new") onChange([...value, row]);
    else onChange(value.map((r, i) => i === editing.index ? row : r));
    setEditing(null);
  }

  const editorBody = editing && (
    <>
      <div className="mb-2">
        <label style={{ fontSize: 12, color: "#555" }}>Index name (optional — auto-generated if blank)</label>
        <input className="form-control" placeholder="e.g. idx_event_user" value={editing.data.name}
          onChange={e => patch({ name: e.target.value })} style={{ fontSize: 13 }} />
      </div>
      <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>Fields (tick all to index together)</label>
      <FieldPillPicker selected={editing.data.fields} onToggle={toggle} availableColumns={availableColumns} />
      {(editing.data.fields || []).length > 0 && (
        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Selected: {editing.data.fields.join(", ")}</div>
      )}
      <div className="mb-2 mt-2" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label style={{ fontSize: 12, color: "#555" }}>Unique index:</label>
        <ThemedToggle checked={editing.data.unique} onChange={val => patch({ unique: val })} onLabel="Unique" offLabel="Non-unique" />
      </div>
    </>
  );

  return (
    <div>
      {value.map((row, idx) => (
        editing && editing.index === idx ? (
          <ConstraintCard key={idx} mode="edit" title="Edit Composite Index"
            onCancel={cancel} onSave={save} saveDisabled={!valid} saveHint={saveHint}>
            {editorBody}
          </ConstraintCard>
        ) : (
          <ConstraintCard key={idx} mode="summary" title={row.name || `Index ${idx + 1}`}
            canEdit={!editing}
            onEdit={() => startEdit(idx)} onRemove={() => remove(idx)}
            summary={
              <span style={{ fontSize: 12, color: "#555" }}>
                {row.unique ? "UNIQUE " : ""}({(row.fields || []).join(", ")})
              </span>
            } />
        )
      ))}

      {editing && editing.index === "new" && (
        <ConstraintCard mode="edit" title="New Composite Index"
          onCancel={cancel} onSave={save} saveDisabled={!valid} saveHint={saveHint}>
          {editorBody}
        </ConstraintCard>
      )}

      {!editing && (
        <button type="button" onClick={startNew}
          style={{ fontSize: 13, color: "#0d6efd", background: "none", border: "1px solid #0d6efd", borderRadius: 6, padding: "4px 14px", cursor: "pointer", marginTop: 4 }}>
          <i className="bi bi-plus-lg" /> Add Composite Index
        </button>
      )}
    </div>
  );
}
